import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Toolbar } from "@/components/layout/Toolbar";
import { StatusBar } from "@/components/layout/StatusBar";
import { InputBar } from "@/components/layout/InputBar";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { QuickActionsToolbar } from "@/components/layout/QuickActionsToolbar";
import { ScrollArea } from "@/components/ui";
import { useAppStore, useConversationsStore, useNavigationStore, useSettingsStore, useVoiceStore } from "@/stores";
import { useHotkey, useMediaQuery } from "@/hooks";
import { ALL_NAV, PRIMARY_NAV } from "@/router/navigation";
import { logger } from "@/services/logger";

/**
 * AppLayout
 *
 * The persistent application frame rendered around every route:
 *
 *   ┌──────────┬───────────────────────────┐
 *   │          │  Toolbar                   │
 *   │ Sidebar  ├───────────────────────────┤
 *   │          │  Page (scrolls)            │
 *   │          ├───────────────────────────┤
 *   │          │  InputBar (chat only)      │
 *   ├──────────┴───────────────────────────┤
 *   │  StatusBar                            │
 *   └───────────────────────────────────────┘
 *
 * It also owns shell-level concerns: syncing the navigation store, global
 * keyboard shortcuts, and auto-collapsing the sidebar on narrow windows.
 */
export function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const visit = useNavigationStore((s) => s.visit);
  const setCollapsed = useAppStore((s) => s.setSidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const setPalette = useAppStore((s) => s.setCommandPaletteOpen);
  const showStatusBar = useSettingsStore((s) => s.showStatusBar);
  const pushToTalkHotkey = useSettingsStore((s) => s.pushToTalkHotkey);
  const toggleRecording = useVoiceStore((s) => s.toggleRecording);

  const isNarrow = useMediaQuery("(max-width: 720px)");

  // Keep the navigation store in step with the router.
  useEffect(() => {
    const match = ALL_NAV.find((n) => pathname.startsWith(n.path));
    if (match) visit(match.id);
  }, [pathname, visit]);

  // Collapse the rail automatically when the window gets tight.
  useEffect(() => {
    if (isNarrow) setCollapsed(true);
  }, [isNarrow, setCollapsed]);

  // Global shortcuts (fixed set → safe to declare individually).
  useHotkey("mod+k", () => setPalette(true));
  useHotkey("mod+b", () => toggleSidebar());
  useHotkey("mod+,", () => navigate("/settings"));
  useHotkey("mod+n", () => {
    useConversationsStore.getState().createConversation();
    navigate("/chat");
  });
  useHotkey(pushToTalkHotkey, () => toggleRecording());

  // Numbered destination jumps (⌘1–⌘4). One listener rather than a hook per
  // item keeps hook order stable and the code honest.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.shiftKey || e.altKey) return;
      const target = PRIMARY_NAV.find((n) => n.shortcut === e.key);
      if (target) {
        e.preventDefault();
        navigate(target.path);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const isChat = pathname.startsWith("/chat");

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-bg-base">
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar />

        <main className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="flex-1">
            <Outlet />
          </ScrollArea>
          {isChat && <InputBar />}
        </main>

        {showStatusBar && <StatusBar />}
      </div>

      <CommandPalette />
      <QuickActionsToolbar />
    </div>
  );
}

logger.debug("layout", "AppLayout module loaded");
